/**
 * HTML-Inhalt und Formatierung für das Sonnen-Detailpanel.
 * Spektraldaten, Alters-Labels, Energie- und Lifetime-Zeilen.
 */

import type { SunLifecycleEntry } from '../systems/SunLifecycleTypes';
import type { SolarEnergyConfig } from '../config/SolarEnergyConfig';
import { SunState } from '../systems/SunLifecycleTypes';
import { ageFactor } from '../systems/SolarEnergyFormulas';

const SPECTRAL_NAMES: Record<string, string> = {
  M: 'Roter Zwerg', K: 'Oranger Zwerg', G: 'Gelber Stern',
  F: 'Wei\u00dfer Stern', O: 'Blauer Riese',
};
const SPECTRAL_ICONS: Record<string, string> = {
  M: '\u{1F534}', K: '\u{1F7E0}', G: '\u2600\uFE0F',
  F: '\u26AA', O: '\u{1F535}',
};

export function spectralIcon(cls: string): string {
  return SPECTRAL_ICONS[cls] ?? '\u2600\uFE0F';
}

export function spectralName(cls: string): string {
  return SPECTRAL_NAMES[cls] ?? cls;
}

export function starName(seed: number): string {
  const L = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const a = L[Math.abs(seed) % L.length];
  const b = L[Math.abs(seed * 7) % L.length];
  const num = (Math.abs(seed) % 900) + 100;
  return `${a}${b}-${num}`;
}

function ageLabel(age: number, alive: boolean): { label: string; color: string } {
  if (!alive) return { label: 'Erloschen', color: '#666' };
  if (age < 0.2) return { label: 'Junger Stern', color: '#8cf' };
  if (age < 0.4) return { label: 'Stabiler Stern', color: '#8cf' };
  if (age < 0.6) return { label: 'Reifer Stern', color: '#fd0' };
  if (age < 0.8) return { label: 'Alter Stern', color: '#fa0' };
  return { label: 'Sterbender Stern', color: '#f44' };
}

function formatTime(sec: number): string {
  if (sec <= 0) return '0';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function lifetimeRow(lc: SunLifecycleEntry | undefined, devMode: boolean): string {
  if (!lc) return '';
  const alive = lc.state === SunState.Alive;
  const { label, color } = ageLabel(lc.age, alive);

  if (!devMode) {
    return (
      `<div style="display:flex;justify-content:space-between;margin-bottom:2px">` +
      `<span style="color:#aaa">Zustand</span>` +
      `<span style="color:${color}">${label}</span></div>` +
      `<div style="display:flex;justify-content:space-between">` +
      `<span style="color:#aaa">Lebensdauer</span>` +
      `<span style="color:#556">??</span></div>`
    );
  }
  const agePct = Math.round(Math.min(lc.age, 1) * 100);
  const remain = alive ? formatTime(lc.remainingSec) : 'Tot';
  return (
    `<div style="display:flex;justify-content:space-between;margin-bottom:2px">` +
    `<span style="color:#aaa">Zustand</span>` +
    `<span style="color:${color}">${label}</span></div>` +
    `<div style="display:flex;justify-content:space-between;margin-bottom:2px">` +
    `<span style="color:#aaa">Alter</span>` +
    `<span style="color:${color}">${agePct}%</span></div>` +
    `<div style="display:flex;justify-content:space-between">` +
    `<span style="color:#aaa">Verbleibend</span>` +
    `<span style="color:${color}">${remain}</span></div>`
  );
}

export function energyRow(
  lc: SunLifecycleEntry | undefined, luminosity: number,
  cfg: SolarEnergyConfig, devMode: boolean,
): string {
  if (!devMode) {
    return (
      `<div style="display:flex;justify-content:space-between;margin-top:2px">` +
      `<span style="color:#aaa">Energie</span>` +
      `<span style="color:#556">??</span></div>`
    );
  }
  if (!lc) return '';
  const alive = lc.state === SunState.Alive;
  const energy = alive ? Math.round(cfg.baseEnergy * luminosity * ageFactor(lc.age, cfg)) : 0;
  const color = alive ? '#fd0' : '#666';
  return (
    `<div style="display:flex;justify-content:space-between;margin-top:2px">` +
    `<span style="color:#aaa">Energie</span>` +
    `<span style="color:${color}">${energy}</span></div>`
  );
}
